// 用户相关API
import apiClient from './config'

/**
 * 用户API类
 */
export class UserApi {
  /**
   * 医生登录
   * 根据新接口文档：http://10.105.0.33:20001/user/login
   * @param {Object} loginData - 登录数据
   * @param {string} loginData.phone - 手机号
   * @param {string} loginData.password - 密码
   * @returns {Promise<Object>} 登录结果
   */
  async login(loginData) {
    try {
      console.log('调用医生登录API:', { phone: loginData.phone, password: '******' })

      // 根据新接口文档，使用POST请求，参数通过Body传递
      const requestBody = {
        phone: loginData.phone,
        password: loginData.password
      }

      const response = await apiClient.post('/user/login', requestBody)

      // 检查响应数据结构
      const data = response.data
      console.log('登录API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        // 登录成功，检查返回数据
        if (!data.data || !data.data.token) {
          console.error('登录成功但缺少必要数据:', data)
          return {
            success: false,
            message: '登录成功但服务器返回数据不完整'
          }
        }

        // 检查用户角色，根据接口文档：1-医生，2-患者，3-管理员
        const roleId = data.data.roleId
        if (roleId !== 1) {
          console.warn('非医生用户尝试登录医生工作台:', {
            roleId,
            name: data.data.name,
            roleName: roleId === 2 ? '患者' : roleId === 3 ? '管理员' : '未知角色'
          })
          return {
            success: false,
            message: `此账号是${roleId === 2 ? '患者' : roleId === 3 ? '管理员' : '未知角色'}账号，无法登录医生工作台`
          }
        }

        // 登录成功，返回用户信息
        console.log('登录成功，后端返回的完整数据:', data.data)

        // 尝试获取用户ID，优先使用后端直接返回的ID
        let userId = data.data.id || data.data.userId

        // 如果后端没有直接返回ID，尝试从token中提取
        if (!userId) {
          userId = this.extractUserIdFromToken(data.data.token)
        }

        console.log('最终获取的用户ID:', userId)

        return {
          success: true,
          message: data.message || '登录成功',
          data: {
            token: data.data.token,
            roleId: data.data.roleId,
            name: data.data.name,
            id: userId
          }
        }
      } else {
        // 登录失败，返回服务器错误信息
        const errorMessage = data.message || '登录失败'
        console.warn('登录失败:', { code: data.code, message: errorMessage })

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('登录API调用失败:', error)

      // 处理不同类型的错误
      if (error.response) {
        // 服务器返回了错误响应
        const status = error.response.status
        const errorData = error.response.data

        let errorMessage = '登录失败'
        if (errorData?.message) {
          errorMessage = errorData.message
        } else if (status === 404) {
          errorMessage = '登录接口不存在，请检查服务器配置'
        } else if (status === 500) {
          errorMessage = '服务器内部错误，请稍后重试'
        } else if (status >= 400 && status < 500) {
          errorMessage = '请求参数错误，请检查输入信息'
        } else {
          errorMessage = `服务器错误 (${status})`
        }

        return {
          success: false,
          message: errorMessage,
          errorCode: status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应（网络问题）
        return {
          success: false,
          message: '网络连接失败，请检查网络设置或联系管理员'
        }
      } else {
        // 其他错误（请求配置错误等）
        return {
          success: false,
          message: error.message || '登录失败，请重试'
        }
      }
    }
  }

  /**
   * 从JWT Token中提取用户信息
   * 根据接口文档示例token格式解析
   * @param {string} token - JWT Token
   * @returns {number|null} 用户ID或null
   */
  extractUserIdFromToken(token) {
    try {
      if (!token) {
        console.warn('Token为空，无法提取用户ID')
        return null
      }

      // JWT token由三部分组成，用.分隔：header.payload.signature
      const parts = token.split('.')
      if (parts.length !== 3) {
        console.warn('Token格式不正确，应该包含3个部分')
        return null
      }

      // 解码payload部分（第二部分）
      const base64Url = parts[1]
      // 将Base64URL转换为标准Base64
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')

      // 安全解码Base64
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )

      const payload = JSON.parse(jsonPayload)
      console.log('Token payload解析结果:', payload)

      // 根据接口文档中的token示例，用户信息在claims对象中
      // 示例：{"claims":{"phone":"15611112222","roleId":2,"id":6},"exp":1752750886}
      const userId = payload.claims?.id

      if (userId) {
        console.log('成功从token中提取用户ID:', userId)
        return userId
      } else {
        console.warn('Token中未找到用户ID，payload结构:', payload)
        return null
      }
    } catch (error) {
      console.error('从token中提取用户ID失败:', error)
      return null
    }
  }

  /**
   * 用户退出登录
   * @returns {Promise<Object>} 退出结果
   */
  async logout() {
    try {
      await apiClient.post('/user/logout')
      return {
        success: true,
        message: '退出成功'
      }
    } catch (error) {
      console.error('退出登录API调用失败:', error)
      // 即使API调用失败，也认为退出成功（清除本地状态）
      return {
        success: true,
        message: '退出成功'
      }
    }
  }

  /**
   * 获取用户信息
   * @returns {Promise<Object>} 用户信息
   */
  async getUserInfo() {
    try {
      const response = await apiClient.get('/user/info')
      const data = response.data

      if (data.code === 0) {
        return {
          success: true,
          data: data.data
        }
      } else {
        return {
          success: false,
          message: data.message || '获取用户信息失败'
        }
      }
    } catch (error) {
      console.error('获取用户信息失败:', error)
      return {
        success: false,
        message: '获取用户信息失败'
      }
    }
  }

  /**
   * 生成医生二维码token
   * @returns {Promise<Object>} 二维码token和过期时间
   */
  async generateDoctorQRCode() {
    try {
      console.log('=== 生成医生二维码token ===')

      const response = await apiClient.post('/user/doctor/generateQRCode')

      console.log('生成二维码API原始响应:', response)

      const data = response.data || response
      console.log('解析后的响应数据:', data)

      if (data.code === 0) {
        console.log('✅ 生成医生二维码token成功')
        console.log('📱 二维码信息:', {
          tokenLength: data.data?.qrCodeToken?.length,
          expireTime: data.data?.expireTime
        })

        return {
          success: true,
          data: {
            qrCodeToken: data.data?.qrCodeToken,
            expireTime: data.data?.expireTime
          },
          message: data.message || '生成二维码成功'
        }
      } else {
        console.error('❌ 生成医生二维码token失败:', data.message)
        return {
          success: false,
          message: data.message || '生成二维码失败'
        }
      }
    } catch (error) {
      console.error('❌ 生成医生二维码token异常:', error)

      // 根据错误类型提供更具体的错误信息
      let errorMessage = '生成二维码失败'
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = '网络连接失败，请检查网络连接'
      } else if (error.message && error.message.includes('500')) {
        errorMessage = '服务器内部错误，请稍后重试'
      } else if (error.message && error.message.includes('401')) {
        errorMessage = '认证失败，请重新登录'
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '权限不足，无法生成二维码'
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }

  /**
   * 发送重置密码验证码
   * 根据接口文档：/user/sms/sendResetCode
   * @param {Object} data - 请求数据
   * @param {string} data.phone - 手机号
   * @returns {Promise<Object>} 发送结果，包含bizId
   */
  async sendResetCode(data) {
    try {
      console.log('发送重置密码验证码:', { phone: data.phone })

      const response = await apiClient.post('/user/sms/sendResetCode', {
        phone: data.phone
      })

      console.log('发送验证码API响应:', response)

      const responseData = response.data
      if (responseData.code === 0) {
        return {
          success: true,
          data: {
            bizId: responseData.data // 接口返回的bizId
          },
          message: responseData.message || '验证码发送成功'
        }
      } else {
        return {
          success: false,
          message: responseData.message || '验证码发送失败'
        }
      }
    } catch (error) {
      console.error('发送验证码失败:', error)

      let errorMessage = '验证码发送失败'
      if (error.response) {
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.message) {
        errorMessage = error.message
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }

  /**
   * 通过短信验证码重置密码
   * 根据接口文档：/user/resetPasswordBySms
   * @param {Object} data - 重置密码数据
   * @param {string} data.phone - 手机号
   * @param {string} data.bizId - 短信验证码业务ID
   * @param {string} data.code - 短信验证码
   * @param {string} data.newPassword - 新密码
   * @param {string} data.confirmPassword - 确认新密码
   * @returns {Promise<Object>} 重置结果
   */
  async resetPasswordBySms(data) {
    try {
      console.log('通过短信验证码重置密码:', {
        phone: data.phone,
        bizId: data.bizId,
        code: data.code,
        newPassword: '******',
        confirmPassword: '******'
      })

      const response = await apiClient.post('/user/resetPasswordBySms', {
        phone: data.phone,
        bizId: data.bizId,
        code: data.code,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword
      })

      console.log('重置密码API响应:', response)

      const responseData = response.data
      if (responseData.code === 0) {
        return {
          success: true,
          data: responseData.data,
          message: responseData.message || '密码重置成功'
        }
      } else {
        return {
          success: false,
          message: responseData.message || '密码重置失败'
        }
      }
    } catch (error) {
      console.error('重置密码失败:', error)

      let errorMessage = '密码重置失败'
      if (error.response) {
        const errorData = error.response.data || {}
        errorMessage = errorData.message || errorData.msg || `服务器错误 (${error.response.status})`
      } else if (error.message) {
        errorMessage = error.message
      }

      return {
        success: false,
        message: errorMessage
      }
    }
  }
}

// 创建用户API实例
export const userApi = new UserApi()

// 默认导出
export default userApi
